// ─── RSVP Surface Shell ───────────────────────────────────────
// Phase RSVP Style 3.0 — three real layers.
//
//   <section .rsvp-section style={transparent, padding}>
//     <RSVPBackgroundLayer target="section" />   ← optional texture/image (legacy)
//     {bgActive && (
//       <div .rsvp-background-layer style={maxWidth, bg-material}>
//         <div .rsvp-surface-layer  style={width%, surface-material}>
//           <div .rsvp-form-content style={form-width}>{children}</div>
//         </div>
//       </div>
//     )}
//     {!bgActive && (
//       <div .rsvp-surface-layer  style={maxWidth, surface-material}>
//         <div .rsvp-form-content style={form-width}>{children}</div>
//       </div>
//     )}
//   </section>
//
// Surface nesting rule (R1): when the Background layer is active, Surface
// width is % of the Background layer's box. When inactive, Surface width is
// % of the variant's natural max-width (current behaviour).

import React from 'react';
import RSVPBackgroundLayer from './RSVPBackgroundLayer';
import {
  isBackgroundLayerActive,
  isSurfaceVisible,
  getBgLayerWidthPercent,
  getSurfaceWidthPercent,
  getContentWidthStyle,
  getRsvpBackgroundLayerStyle,
  getRsvpSurfaceStyle,
  getSurfaceAlpha,
  getBgLayerAlpha,
  getBackdropBlur,
  getBgLayerRadiusPx,
  getSurfaceRadiusPx,
} from '../../../lib/utils/responseCardStyle';

export default function RsvpSurfaceShell({
  cs = {},
  background,
  naturalMaxWidthRem = 42,
  borderRadiusRem = 1.25,
  contentPaddingClass = 'p-6 sm:p-8',
  sectionPaddingClass = 'px-5 py-20',
  surfaceBgColor,
  surfaceBorderColor,
  legacyShadowClass = '',
  withLegacyBorder = false,
  rsvpRef,
  children,
  contentAlign = 'left',
}) {
  const bgActive = isBackgroundLayerActive(cs);
  const surfaceVisible = isSurfaceVisible(cs);
  const surfaceAlpha = getSurfaceAlpha(cs);
  const surfaceBlur = getBackdropBlur(cs);
  // Section-level artwork opacity. When Background layer is active, its opacity
  // multiplies the section image so bgOpacity=0 means "no background visible at
  // all" (matches user expectation: one Background slider controls everything
  // behind the surface). When Background is None, image keeps its own opacity.
  const sectionImageAlpha = bgActive ? getBgLayerAlpha(cs) : 1;

  // ─── Background layer (outermost when active) ────────────
  // Width: 100% = section content width. When bgActive we remove the section's
  // horizontal padding so 100% reaches the viewport edges (true full-bleed).
  const bgWidthPct = getBgLayerWidthPercent(cs);
  const bgLayerMaterial = getRsvpBackgroundLayerStyle(cs, {
    backgroundColor: surfaceBgColor || undefined,
    borderColor: surfaceBorderColor || undefined,
  });

  // ─── Surface layer (inner card) ──────────────────────────
  // When bg is active: width = % of bg layer (parent).
  // When bg is inactive: width = % of section / variant natural max-width.
  const surfaceWidthPct = getSurfaceWidthPercent(cs);
  const surfaceWidthStyle = bgActive
    ? { width: `${surfaceWidthPct}%`, maxWidth: '100%' }
    : (surfaceWidthPct < 100
        ? { width: `${surfaceWidthPct}%`, maxWidth: '100%' }
        : null);
  const surfaceMaterial = surfaceVisible
    ? getRsvpSurfaceStyle(cs, {
        backgroundColor: surfaceBgColor || undefined,
        borderColor: surfaceBorderColor || undefined,
      })
    : {};

  // Border for the inner surface.
  const surfaceShowBorder =
    surfaceVisible && (withLegacyBorder || surfaceAlpha < 1 || surfaceBlur > 0);
  const surfaceBorderProp = surfaceShowBorder && surfaceMaterial.borderColor
    ? { border: '1px solid' }
    : {};

  // Legacy shadow class only when designer hasn't set a custom surface shadow.
  const surfaceShadowSet = surfaceMaterial.boxShadow !== undefined;

  const contentWidthStyle = getContentWidthStyle(cs);

  // ─── Compose ─────────────────────────────────────────────
  const formContent = (
    <div
      className={`relative z-10 ${contentPaddingClass}`}
      data-testid="rsvp-form-content-wrapper"
    >
      <div
        className={`mx-auto ${contentAlign === 'center' ? 'text-center' : ''}`}
        style={contentWidthStyle || undefined}
      >
        {children}
      </div>
    </div>
  );

  // Build the surface-layer wrapper. When the surface is hidden we still
  // render a transparent wrapper to host the form column at its width.
  const surfaceWrapper = (
    <div
      className={`mx-auto relative rsvp-bg-host ${!surfaceShadowSet && surfaceVisible ? legacyShadowClass : ''}`}
      style={{
        ...(surfaceWidthStyle || {}),
        ...(bgActive ? {} : { maxWidth: `${naturalMaxWidthRem}rem` }),
        borderRadius: surfaceVisible
          ? (getSurfaceRadiusPx(cs) !== null ? `${getSurfaceRadiusPx(cs)}px` : `${borderRadiusRem}rem`)
          : 0,
        overflow: surfaceVisible ? 'hidden' : undefined,
        ...(surfaceVisible
          ? {
              ...surfaceMaterial,
              ...surfaceBorderProp,
            }
          : { background: 'transparent' }),
      }}
      data-testid="rsvp-surface-layer"
    >
      {/* Card-level texture/image/wash only paints when there IS a surface
          to host it. With Surface=None we skip it entirely so washes can't
          ghost-tint the area when the rest of the surface is invisible. */}
      {surfaceVisible && (
        <RSVPBackgroundLayer background={background} target="card" surfaceAlpha={surfaceAlpha} />
      )}
      {formContent}
    </div>
  );

  return (
    <section
      ref={rsvpRef}
      className={`${bgActive ? 'py-20' : sectionPaddingClass} rsvp-bg-host`}
      style={{ background: 'transparent' }}
      data-testid="rsvp-section"
    >
      <RSVPBackgroundLayer background={background} target="section" surfaceAlpha={sectionImageAlpha} />

      {bgActive ? (
        <div
          className="mx-auto relative rsvp-bg-host"
          style={{
            width: `${bgWidthPct}%`,
            borderRadius: getBgLayerRadiusPx(cs) !== null ? `${getBgLayerRadiusPx(cs)}px` : `${borderRadiusRem}rem`,
            overflow: 'hidden',
            ...bgLayerMaterial,
          }}
          data-testid="rsvp-background-layer"
        >
          <div className="p-4 sm:p-6">{surfaceWrapper}</div>
        </div>
      ) : (
        surfaceWrapper
      )}
    </section>
  );
}
